import * as Sentry from '@sentry/react';

// The optimizer runs Pyodide on a single worker thread. If that thread is busy
// (or a worker message is dropped) a call can hang indefinitely, leaving the
// dashboard stuck. Bound these waits so we surface an error instead of hanging.
export const OPTIMIZER_LOOKUP_TIMEOUT_MS = 10_000;
export const OPTIMIZER_PLAN_TIMEOUT_MS = 60_000;

export class OptimizerTimeoutError extends Error {
    readonly timeoutMs: number;
    readonly label: string;

    constructor(label: string, timeoutMs: number) {
        super(`${label} timed out after ${timeoutMs}ms`);
        this.name = 'OptimizerTimeoutError';
        this.label = label;
        this.timeoutMs = timeoutMs;
    }
}

/**
 * Type guard for an optimizer-stale-call timeout. Use this to distinguish an
 * *expected* worker hang (which we recover from with a friendly, navigate-away
 * screen) from an unexpected error (which falls through to the generic error
 * boundary). Checks `name` as well so the guard survives error serialization
 * across the worker/Comlink boundary, where the prototype chain is lost.
 */
export const isOptimizerTimeoutError = (
    error: unknown,
): error is OptimizerTimeoutError =>
    error instanceof OptimizerTimeoutError ||
    (error instanceof Error && error.name === 'OptimizerTimeoutError');

/**
 * Wraps a stale-prone optimizer call with a timeout. When the call exceeds
 * `timeoutMs` an {@link OptimizerTimeoutError} is reported to Sentry (tagged so
 * stale optimizer calls can be tracked separately) and thrown so the caller can
 * decide how to recover — degrade to a fallback or surface an error response.
 */
export const withOptimizerTimeout = async <T>(
    promise: Promise<T>,
    label: string,
    timeoutMs: number = OPTIMIZER_LOOKUP_TIMEOUT_MS,
): Promise<T> => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const timeout = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
            const error = new OptimizerTimeoutError(label, timeoutMs);

            Sentry.captureException(error, {
                level: 'error',
                tags: {
                    feature: 'optimizer',
                    reason: 'optimizer_stale_call',
                },
                extra: { label, timeoutMs },
            });

            reject(error);
        }, timeoutMs);
    });

    try {
        return await Promise.race([promise, timeout]);
    } finally {
        if (timeoutId !== undefined) {
            clearTimeout(timeoutId);
        }
    }
};
